import awsconfig from '../aws-exports';
import Amplify from 'aws-amplify';

Amplify.configure(awsconfig);

const SHOPIFY_STOREFRONT_API = `https://${process.env.GATSBY_SHOP_NAME}.myshopify.com/api/2024-10/graphql`;
const SHOPIFY_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Shopify-Storefront-Access-Token': process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
};

const storefrontFetch = async (query, variables = {}) => {
  try {
    const response = await fetch(SHOPIFY_STOREFRONT_API, {
      method: 'POST',
      headers: SHOPIFY_HEADERS,
      body: JSON.stringify({ query, variables }),
    });
    const json = await response.json();
    if (json.errors) {
      console.error('Shopify storefront errors:', json.errors);
    }
    return json;
  } catch (error) {
    console.error('Shopify storefront fetch failed:', error);
    return { errors: [error] };
  }
};

export const getCustomerToken = async () => {
  try {
    const user = await Amplify.Auth.currentUserInfo();
    if (!user) {
      return undefined;
    }

    const email = user.attributes.email;
    const pass = user.attributes.sub.substring(0, 13);
    const query = `mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
      customerAccessTokenCreate(input: $input) {
        customerAccessToken {
          accessToken
          expiresAt
        }
        customerUserErrors {
          code
          field
          message
        }
      }
    }`;

    const result = await storefrontFetch(query, {
      input: { email, password: pass },
    });

    return result?.data?.customerAccessTokenCreate?.customerAccessToken?.accessToken;
  } catch (error) {
    console.log('Got error while trying to get customer token', error);
    return undefined;
  }
};

export const getCustomer = async () => {
  try {
    const customerAccessToken = await getCustomerToken();
    if (!customerAccessToken) {
      return undefined;
    }

    const query = `query customer($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        id
        lastIncompleteCheckout {
          id
          webUrl
        }
      }
    }`;

    const result = await storefrontFetch(query, { customerAccessToken });
    return result?.data?.customer;
  } catch (error) {
    console.log('Got error while trying to get customer details', error);
    return undefined;
  }
};

export const createCart = async (lines = [], attributes = []) => {
  try {
    const query = `mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
          lines(first: 20) {
            edges {
              node {
                id
                quantity
              }
            }
          }
          attributes {
            key
            value
          }
        }
        userErrors {
          field
          message
        }
      }
    }`;

    const result = await storefrontFetch(query, { input: { lines, attributes } });
    return result?.data?.cartCreate?.cart;
  } catch (error) {
    console.log('Got error while trying to create cart', error);
    return undefined;
  }
};

export const addCartLines = async (cartId, lines = []) => {
  try {
    const query = `mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          lines(first: 20) {
            edges {
              node {
                id
                quantity
              }
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }`;

    const result = await storefrontFetch(query, { cartId, lines });
    return result?.data?.cartLinesAdd;
  } catch (error) {
    console.log('Got error while trying to add cart lines', error);
    return undefined;
  }
};

export const updateCartAttributes = async (cartId, attributes = []) => {
  try {
    const query = `mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
      cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
        cart {
          id
          checkoutUrl
          attributes {
            key
            value
          }
        }
        userErrors {
          field
          message
        }
      }
    }`;

    const result = await storefrontFetch(query, { cartId, attributes });
    return result?.data?.cartAttributesUpdate;
  } catch (error) {
    console.log('Got error while trying to update cart attributes', error);
    return undefined;
  }
};

export const updateCartNote = async (cartId, note) => {
  try {
    const query = `mutation cartNoteUpdate($cartId: ID!, $note: String!) {
      cartNoteUpdate(cartId: $cartId, note: $note) {
        cart {
          id
          checkoutUrl
          note
        }
        userErrors {
          field
          message
        }
      }
    }`;

    const result = await storefrontFetch(query, { cartId, note });
    return result?.data?.cartNoteUpdate;
  } catch (error) {
    console.log('Got error while trying to update cart note', error);
    return undefined;
  }
};

export const updateCartBuyerIdentity = async (cartId, customerAccessToken) => {
  try {
    const query = `mutation cartBuyerIdentityUpdate($cartId: ID!, $customerAccessToken: String!) {
      cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: { customerAccessToken: $customerAccessToken }) {
        cart {
          id
          checkoutUrl
          buyerIdentity {
            customerAccessToken
          }
        }
        userErrors {
          field
          message
        }
      }
    }`;

    const result = await storefrontFetch(query, { cartId, customerAccessToken });
    return result?.data?.cartBuyerIdentityUpdate;
  } catch (error) {
    console.log('Got error while trying to update cart buyer identity', error);
    return undefined;
  }
};

export const getCheckout = async (checkoutId) => {
  try {
    const query = `query cart($id: ID!) {
      cart(id: $id) {
        id
        checkoutUrl
        createdAt
        updatedAt
        lines(first: 20) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
            }
          }
        }
        attributes {
          key
          value
        }
      }
    }`;

    const result = await storefrontFetch(query, { id: checkoutId });
    return result?.data?.cart;
  } catch (error) {
    console.log('Got error while trying to get checkout details', error);
    return undefined;
  }
};
